import { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * ExpandableText — collapse long text at a soft character budget, reveal on click.
 *
 * Props:
 *   text        : the full text
 *   limit       : chars shown while collapsed (default 320)
 *   threshold   : min extra chars beyond `limit` before a toggle appears (default 30)
 *   className   : classes for the <p>
 *   testId      : data-testid prefix — "-toggle", "-body" appended
 *
 * Behaviour:
 *   - If text ≤ limit + threshold → render as-is, no toggle (avoids ellipsis for +5 chars).
 *   - Otherwise render a truncated version + "Read more" button that expands in-place.
 */
export const ExpandableText = ({
  text,
  limit = 320,
  threshold = 30,
  className = 'text-sm text-white/70 leading-relaxed whitespace-pre-line',
  testId,
}) => {
  const [expanded, setExpanded] = useState(false);

  const needsToggle = useMemo(() => {
    if (!text) return false;
    return text.length > limit + threshold;
  }, [text, limit, threshold]);

  // Cut at the last space before `limit` so we don't slice mid-word — computed unconditionally.
  const collapsed = useMemo(() => {
    if (!text || !needsToggle) return text || '';
    let cut = text.slice(0, limit);
    const lastSpace = cut.lastIndexOf(' ');
    if (lastSpace > limit * 0.7) cut = cut.slice(0, lastSpace);
    return `${cut.trimEnd()}…`;
  }, [text, limit, needsToggle]);

  if (!text) return null;

  return (
    <div>
      <p className={className} data-testid={testId ? `${testId}-body` : undefined}>
        {expanded || !needsToggle ? text : collapsed}
      </p>
      {needsToggle && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={testId ? `${testId}-toggle` : undefined}
          className="mt-2 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#00b4d8] hover:text-[#c8e600] transition-colors"
        >
          {expanded ? (<>Read less <ChevronUp className="h-3.5 w-3.5" /></>) : (<>Read more <ChevronDown className="h-3.5 w-3.5" /></>)}
        </button>
      )}
    </div>
  );
};

export default ExpandableText;
